import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
    VisibilityState,
} from "@tanstack/react-table";
import { Cross2Icon } from "@radix-ui/react-icons";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ApprovalType, ApprovalTypeLabels } from "@/models/leave-requests.model";
import { useLeaveTypes } from "@/context/LeaveTypesContext";
import { capitalizeFirstLetter } from "@/utils/string-format";
import { LeaveType } from "@/models/leave-types.model";
import { useAppDispatch } from "@/app/hooks";
import { UserDataTableFacetedFilter } from "./UserDataTableFacetedFilter";

const ApprovalTypeOptions = Object.values(ApprovalType).map((type) => ({
    label: ApprovalTypeLabels[type],
    value: type
}));

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    containerClassname?: string;
    typesSelected: string[];
    setTypesSelected: Dispatch<SetStateAction<string[]>>;
    statusSelected: string[];
    setStatusSelected: Dispatch<SetStateAction<string[]>>;
    onResetFilters?: () => void;
}

function UserDataTableWithToolbar({
    columns,
    data,
    containerClassname,
    typesSelected,
    setTypesSelected,
    statusSelected,
    setStatusSelected,
    onResetFilters
}: DataTableProps<any, any>) {
    const dispatch = useAppDispatch();
    const [sorting, setSorting] = useState<SortingState>([]);
    const { leaveTypes } = useLeaveTypes();
    const [isFiltered, setIsFiltered] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
    const [leaveTypeOptions, setLeaveTypeOptions] = useState<
        {
            label: string;
            value: string;
        }[]
    >([]);

    // Setup leave type options
    useEffect(() => {
        setLeaveTypeOptions(
            leaveTypes.map((leaveType: LeaveType) => ({
                label: capitalizeFirstLetter(leaveType.name),
                value: leaveType.id,
            }))
        );
    }, [leaveTypes]);

    // Update filtered state whenever the selections change
    useEffect(() => {
        setIsFiltered(typesSelected.length > 0 || statusSelected.length > 0);
    }, [typesSelected, statusSelected]);

    // Handle reset button click
    const handleResetButton = () => {
        if (onResetFilters) {
            onResetFilters();
        } else {
            setTypesSelected([]);
            setStatusSelected([]);
        }
    };

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnVisibility,
        },
    });

    return (
        <div className={`flex flex-col w-full h-full ${containerClassname || ""}`}>
            {/* Toolbar with sticky positioning */}
            <div className="sticky top-0 bg-white z-20 pb-2 w-full">
                <div className="flex flex-wrap items-center justify-between h-[60px] ml-1 mb-2">
                    <div className="flex flex-1 flex-wrap items-center gap-2 h-full">
                        <UserDataTableFacetedFilter
                            selectedValues={typesSelected}
                            setSelectedValues={setTypesSelected}
                            options={leaveTypeOptions}
                            title="Leave Type"
                        />
                        <UserDataTableFacetedFilter
                            selectedValues={statusSelected}
                            setSelectedValues={setStatusSelected}
                            options={ApprovalTypeOptions}
                            title="Status"
                        />
                        {isFiltered && (
                            <Button
                                variant="ghost"
                                onClick={handleResetButton}
                                className="h-8 px-2 lg:px-3"
                            >
                                Reset
                                <Cross2Icon className="ml-2 h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table container that fills available space and handles overflow */}
            <div className="flex-1 overflow-auto">
                <Table className="min-w-[800px]">
                    <TableHeader className="sticky top-0 bg-white z-1">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} className={`whitespace-nowrap w-[${header.column.columnDef.size}px]`}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    className="text-sm"
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell

                                            key={cell.id}
                                            className={`whitespace-nowrap w-[${cell.column.columnDef.size}px]`}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="h-24 text-center">
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div >
    );
}

export default UserDataTableWithToolbar;